/**
 * Lesende Zusatzliste eines Datensatzes (REQ-27 §2.2): Positionen und Rechnungen eines
 * Containers. Geladen wird erst, wenn der Reiter zum ersten Mal offen ist — die Schublade
 * löst damit keine Abfrage aus, die der Benutzer nie zu sehen bekommt.
 *
 * Kein Zwischenspeicher über die Lebensdauer hinaus: Wie im Altclient wird bei jeder
 * Zustandsänderung neu geladen (REQ-00 §2).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface RelatedList<T> {
  items: readonly T[];
  loading: boolean;
  /** `null`, solange nichts schiefgegangen ist. */
  error: unknown;
  /** Lädt erneut — hinter „Erneut versuchen" des Fehlerzustands. */
  retry: () => void;
  /** Anzahl erst nach erfolgreichem Laden; davor zeigt der Reiter keine Zahl. */
  count: number | undefined;
}

interface ListState<T> {
  items: readonly T[];
  loading: boolean;
  error: unknown;
  count: number | undefined;
}

/** Ausgangszustand als Konstante: `setState` damit lässt React ohne Neuzeichnen stehen. */
const EMPTY: ListState<never> = { items: [], loading: false, error: null, count: undefined };

export function useRelatedList<T>(
  /**
   * Datensatz, zu dem die Liste gehört; ein Wechsel verwirft den geladenen Stand. „Refresh"
   * ändert den Schlüssel mit und lädt die Liste damit ebenfalls neu.
   */
  key: number | string,
  load: () => Promise<T[]>,
  /** `true`, sobald der Reiter offen ist. */
  active: boolean,
): RelatedList<T> {
  // `load` schließt über den Datensatz und wechselt bei jedem Rendern seine Identität.
  const latest = useRef(load);
  latest.current = load;
  // Verwirft Antworten überholter Ladevorgänge.
  const generation = useRef(0);
  // Gesetzt, sobald ein Ladevorgang begonnen hat — noch bevor der Zustand davon weiß.
  const started = useRef(false);
  const [state, setState] = useState<ListState<T>>(EMPTY);

  const run = useCallback(() => {
    generation.current += 1;
    const current = generation.current;
    started.current = true;
    setState({ items: [], loading: true, error: null, count: undefined });

    latest.current().then(
      (items) => {
        if (generation.current !== current) return;
        setState({ items, loading: false, error: null, count: items.length });
      },
      (error: unknown) => {
        if (generation.current !== current) return;
        setState({ items: [], loading: false, error, count: undefined });
      },
    );
  }, []);

  // Ein anderer Datensatz: Der geladene Stand gilt nicht mehr.
  useEffect(() => {
    generation.current += 1;
    started.current = false;
    setState(EMPTY);
  }, [key]);

  useEffect(() => {
    if (!active || started.current) return;
    run();
  }, [active, key, run]);

  return {
    items: state.items,
    loading: state.loading,
    error: state.error,
    count: state.count,
    retry: run,
  };
}
